import * as React from 'react';
import { FilterField, FilterSection } from './FilterSection';
import { FilterSelect } from './FilterSelect';
import { CARE_MODEL_OPTIONS, RISK_OPTIONS } from '../constants';
import { RosterFilters } from './types';

/**
 * Clinical status of the member.
 *
 * The old "Progress" pills (New / On Track / Off Track / Drop-off Risk /
 * Inactive) are deliberately gone: they encoded the single-provider care model
 * this redesign replaces, and the Care Coordination section now covers the
 * same funnel with statuses that match how care actually gets delivered.
 *
 * Outcome Goal was cut from here in round 8, per team feedback — it had
 * already lost its roster column in round 7 (see `columnHeaders` in
 * columns.ts), so as of this round it's neither visible nor filterable
 * anywhere on the roster. `outcome_goal_status` stays a real field on
 * `MockedRosterFields` — this is a filter removal, not a data removal, same
 * as the Care Team roles cut alongside it this round.
 *
 * Care Model is single-select (Aug 2026 filter-logic pass) — a member has one
 * care model, so multi-select only ever expressed an OR nobody asked for.
 * Risk Stratification stays multi-select: "High or Medium" is a real question
 * a care team asks, unlike "Collab or Coach-Only".
 */
export const MemberStatusSection = ({ filters }: { filters: RosterFilters }) => (
  <FilterSection title="Member Status">
    <FilterField label="Care Model">
      <FilterSelect
        isMulti={false}
        placeholder="All"
        options={CARE_MODEL_OPTIONS}
        selected={filters.selected.careModels}
        onChange={(v) => filters.setFilter('careModels', v)}
      />
    </FilterField>

    <FilterField label="Risk Stratification">
      <FilterSelect
        placeholder="All"
        options={RISK_OPTIONS}
        selected={filters.selected.risks}
        onChange={(v) => filters.setFilter('risks', v)}
      />
    </FilterField>

    {/*
      Team feedback (Aug 2026) asked to design for an Active / Inactive /
      Drop-off Risk engagement axis without shipping it yet — there's no
      honest signal to derive "Active" from today (no messaging-activity
      field). This section is a plain stack of FilterFields, so adding that
      group here later — right after Risk Stratification, matching where the
      old Progress pills sat — is additive, not a layout change.
    */}
  </FilterSection>
);
